import { safeLocalStorage } from "../utils/safeStorage";
import { normalizeFlyerInstanceId } from "./flyerInstanceIds";

export { normalizeFlyerInstanceId };

export const PENDING_REFERRAL_CODE_STORAGE_KEY = "pendingReferralCode";
export const PENDING_REFERRAL_ATTRIBUTION_STORAGE_KEY =
  "pendingReferralAttributionV2";

export const QR_FLYER_REFERRAL_CHANNEL = "qr_senior_flyer_v2";
export const QR_FLYER_AD_SOURCE = "Poster / Flyer";
export const QR_FLYER_SOURCE = "qr";
export const QR_FLYER_SOURCE_FAMILY = "other";
export const QR_FLYER_MEDIUM = "poster";
export const QR_FLYER_CAMPAIGN = "senior_flyer_v2";
export const QR_FLYER_REFERRAL_SOURCE = "helper_senior_flyer_v2";
export const QR_FLYER_VERSION = "2";
export const UNKNOWN_QR_FLYER_INSTANCE_ID = "unknown";
export const QR_FLYER_ATTRIBUTION = Object.freeze({
  source: QR_FLYER_SOURCE,
  sourceFamily: QR_FLYER_SOURCE_FAMILY,
  medium: QR_FLYER_MEDIUM,
  campaign: QR_FLYER_CAMPAIGN,
  campaignId: QR_FLYER_CAMPAIGN,
  channel: QR_FLYER_REFERRAL_CHANNEL,
  referralSource: QR_FLYER_REFERRAL_SOURCE,
  flyerVersion: QR_FLYER_VERSION,
});

export const normalizeReferralCode = (value = "") =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");

export const isValidReferralCode = (code = "") => {
  const normalized = normalizeReferralCode(code);
  const letters = (normalized.match(/[a-z]/g) || []).length;
  const numbers = (normalized.match(/[0-9]/g) || []).length;

  return normalized.length >= 6 && letters >= 4 && numbers >= 2;
};

export const loadPendingReferralCode = (
  storageKey = PENDING_REFERRAL_CODE_STORAGE_KEY,
) => normalizeReferralCode(safeLocalStorage.getItem(storageKey) || "");

export const savePendingReferralCode = (
  value = "",
  storageKey = PENDING_REFERRAL_CODE_STORAGE_KEY,
) => {
  const normalized = normalizeReferralCode(value);

  if (!normalized) {
    safeLocalStorage.removeItem(storageKey);
    return "";
  }

  safeLocalStorage.setItem(storageKey, normalized);
  return normalized;
};

export const clearPendingReferralCode = (
  storageKey = PENDING_REFERRAL_CODE_STORAGE_KEY,
) => {
  safeLocalStorage.removeItem(storageKey);
};

export const buildQrFlyerReferralAttribution = ({
  referralCode = "",
  flyerVersion = QR_FLYER_VERSION,
  flyerInstanceId = "",
  capturedAtMs = Date.now(),
} = {}) => {
  const normalizedFlyerInstanceId = normalizeFlyerInstanceId(flyerInstanceId);

  return {
    ...QR_FLYER_ATTRIBUTION,
    flyerVersion:
      String(flyerVersion || QR_FLYER_VERSION)
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9]/g, "")
        .slice(0, 40) || QR_FLYER_VERSION,
    referralCode: normalizeReferralCode(referralCode),
    ...(normalizedFlyerInstanceId
      ? { flyerInstanceId: normalizedFlyerInstanceId }
      : {}),
    firstCapturedAtMs: Number(capturedAtMs || Date.now()),
    lastCapturedAtMs: Number(capturedAtMs || Date.now()),
  };
};

export const loadPendingReferralAttribution = (
  storageKey = PENDING_REFERRAL_ATTRIBUTION_STORAGE_KEY,
) => {
  const raw = safeLocalStorage.getItem(storageKey);
  if (!raw) return null;

  try {
    const parsed = JSON.parse(raw);
    const referralCode = normalizeReferralCode(parsed?.referralCode || "");
    if (!referralCode) return null;
    return {
      ...parsed,
      referralCode,
      source: String(parsed?.source || "").trim().toLowerCase(),
      channel: String(parsed?.channel || "").trim().toLowerCase(),
    };
  } catch (_error) {
    safeLocalStorage.removeItem(storageKey);
    return null;
  }
};

export const savePendingReferralAttribution = (
  attribution = null,
  storageKey = PENDING_REFERRAL_ATTRIBUTION_STORAGE_KEY,
) => {
  const referralCode = normalizeReferralCode(attribution?.referralCode || "");
  if (!attribution || !referralCode) {
    safeLocalStorage.removeItem(storageKey);
    return null;
  }

  const existing = loadPendingReferralAttribution(storageKey);
  const now = Number(attribution.lastCapturedAtMs || Date.now());
  const next = {
    ...attribution,
    referralCode,
    firstCapturedAtMs: Number(existing?.firstCapturedAtMs || now),
    lastCapturedAtMs: now,
  };

  safeLocalStorage.setItem(storageKey, JSON.stringify(next));
  return next;
};

export const clearPendingReferralAttribution = (
  storageKey = PENDING_REFERRAL_ATTRIBUTION_STORAGE_KEY,
) => {
  safeLocalStorage.removeItem(storageKey);
};

export const isQrFlyerReferralAttribution = (attribution = null) => {
  const hasQrChannel =
    String(attribution?.channel || "").trim().toLowerCase() ===
      QR_FLYER_REFERRAL_CHANNEL ||
    (String(attribution?.source || "").trim().toLowerCase() === "qr" &&
      String(attribution?.medium || "").trim().toLowerCase() === "poster");

  return hasQrChannel;
};

export const hasPendingQrFlyerReferralAttribution = ({
  referralCode = "",
  storageKey = PENDING_REFERRAL_ATTRIBUTION_STORAGE_KEY,
} = {}) => {
  const attribution = loadPendingReferralAttribution(storageKey);
  if (!isQrFlyerReferralAttribution(attribution)) return false;
  const normalized = normalizeReferralCode(referralCode);
  return !normalized || attribution.referralCode === normalized;
};

export const getPendingReferralApplyChannel = ({
  referralCode = "",
  fallback = "url",
  storageKey = PENDING_REFERRAL_ATTRIBUTION_STORAGE_KEY,
} = {}) => {
  const normalized = normalizeReferralCode(referralCode);
  const attribution = loadPendingReferralAttribution(storageKey);
  if (
    attribution &&
    (!normalized || attribution.referralCode === normalized) &&
    isQrFlyerReferralAttribution(attribution)
  ) {
    return QR_FLYER_REFERRAL_CHANNEL;
  }
  return fallback;
};
